/**
 * Conversation Page Template (100% Shared)
 *
 * Platform-agnostic conversation page template using facade pattern.
 * Displays conversation messages, handles user input, and manages drawing creation.
 *
 * DRAMATICALLY SIMPLIFIED with Facade Pattern!
 *
 * Before: 883 lines with 15+ hooks, complex state management
 * After: ~400 lines with 1 facade + platform-specific props
 *
 * The facade encapsulates all shared logic:
 * - State (messages, isLoading, isStreaming, headers)
 * - Streaming (submitMessage, cancelStreaming, loadHistory)
 * - Drawing (createDrawing, cancelDrawing, processHeaders)
 * - Headers (clarification, reflection, repeated, readyToCreate)
 * - Project (create, metadata)
 * - Navigation (toConversation, toProjectDetails)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { ConversationPageFacade } from '@ideoz/core';
import { ApiUrls, useCreditLimitBanner, useTrackEvent } from '@ideoz/core';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import {
  Header,
  showToast,
  LoadingOverlay,
  AuthDialog,
  ConfirmProjectModal,
  AnimatedBanner,
  MessageList,
  ConversationSkeleton,
  GoToBottomButton,
  CreateFreeAccountConversationDialog,
  SideMenu,
} from '../index';
import { useFileAttachments } from '../../hooks/useFileAttachments';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import { ChatTextAreaInternal as ChatTextArea } from '../molecules/ChatTextArea';
import { Sparkles, FolderPlus, CirclePlus } from 'lucide-react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../atoms/resizable';
import { PrototyperPanel } from '../organisms/PrototyperPanel';
import { DesignInsightsPanel } from '../organisms/DesignInsightsPanel';
import { hasArabicCharacters } from '../../utils/messageFormatting';
import FigJamPluginDialog from '../molecules/FigJamPluginDialog';
import { cn } from '@ideoz/ui';
import { UpgradeCtaCard } from '../molecules/UpgradeCtaCard';
import UpgradeToSmartDialog from '../molecules/UpgradeToSmartDialog';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Interface for openConversationAndDrawing user action (Plugin Environment)
 */
interface OpenConversationAndDrawingAction {
  type: 'openConversationAndDrawing';
  generateType: string;
  conversationID: string;
  conversationName: string;
  projectId?: string;
  timestamp?: string;
  hiddenMessage?: string;
}

/**
 * Conversation Page Template Props
 *
 * Simplified with facade pattern - only 1 main prop + platform-specific overrides
 */
export interface ConversationPageTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: ConversationPageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Web-specific auth dialog
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    isLoading: boolean;
    openRegister: () => void;
    openLogin: () => void;
    closeDialog: () => void;
    switchMode: (mode: 'login' | 'register') => void;
    handleLogin: (email: string, password: string) => Promise<void>;
    handleRegister: (name: string, email: string, password: string) => Promise<void>;
    handleGoogleAuth: () => Promise<void>;
    handleNavigateToForgotPassword: () => void;
  };

  // Plugin-specific auth
  pluginLogin?: any;
  pluginStopInterval?: () => void;
  isAuthLoading?: boolean;

  // Optional: URL conversation ID from routing
  urlConversationId?: string | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ConversationPageTemplate({
  facade,
  isWeb,
  authDialogState,
  pluginLogin,
  pluginStopInterval,
  isAuthLoading = false,
  urlConversationId,
}: ConversationPageTemplateProps) {
  const { trackEvent } = useTrackEvent();
  // Use onboarding flag from Redux facade
  const isOnboardingUser = facade.state.isOnboardingPrototyper === true;
  // Design Insights Panel state
  const [isDesignInsightsPanelOpen, setIsDesignInsightsPanelOpen] = useState(false);
  // ========== Local UI State ==========
  const [showConversation, setShowConversation] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const creditLimitBanner = useCreditLimitBanner();
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
  const [showProjectHint, setShowProjectHint] = useState(false);
  const [waitingForAuth, setWaitingForAuth] = useState(false);

  // Split screen state
  const [isPrototypeOpen, setIsPrototypeOpen] = useState(false);
  const PROTOTYPE_PANEL_DEFAULT_PERCENT = 70;
  const CONVERSATION_PANEL_DEFAULT_PERCENT = 30;
  const [isConversationPanelCollapsed, setIsConversationPanelCollapsed] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [targetAction, setTargetAction] = useState<string | null>(null);
  const [isCreateFreeAccountDialogOpen, setIsCreateFreeAccountDialogOpen] = useState(false);
  const [isLoadingProjectMetadata, setIsLoadingProjectMetadata] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isFigJamPluginDialogOpen, setIsFigJamPluginDialogOpen] = useState(false);
  const [chatBoxHeight, setChatBoxHeight] = useState(112); // Default height in pixels
  const [isUpgradeToSmartDialogOpen, setIsUpgradeToSmartDialogOpen] = useState(false);
  const [hasProcessedUserAction, setHasProcessedUserAction] = useState(false);

  const conversationContainerRef = useRef<HTMLDivElement>(null);
  const chatBoxContainerRef = useRef<HTMLDivElement>(null);
  const loadedConversationsRef = useRef<Set<string>>(new Set());
  const hasInitializedRef = useRef(false);
  const hasCreatedPrototypeRef = useRef(false);
  const conversationPanelRef = useRef<ImperativePanelHandle | null>(null);
  const prototypePanelRef = useRef<ImperativePanelHandle | null>(null);
  // Track if prototype was initiated from uncover mode (to prevent mode switch in menu)
  const initiatedFromUncoverRef = useRef(false);

  // ========== Extract Facade State ==========
  const {
    isLoggedIn,
    currentProjectId,
    currentConversationId,
    messages,
    isLoading,
    isLoadingDetails,
    isStreaming,
    name,
    session_id,
    init_message,
    skip_analysis,
    initialAttachmentIds,
    isActive,
    llmFamily,
    planDetails,
    initiatedFromUncover,
  } = facade.state;

  /**
   * Check if user has reached project limit
   * Returns true if balance >= maxLimit and maxLimit is not "unlimited"
   */
  const hasReachedProjectLimit = useCallback(() => {
    if (!planDetails?.features) return false;

    const projectsFeature = planDetails.features.find((f: any) => f.name === 'Projects');

    if (!projectsFeature) return false;

    const maxLimit = projectsFeature.maxLimit;
    const balance = projectsFeature.balance ?? 0;

    // If maxLimit is "unlimited", user hasn't reached limit
    if (maxLimit === 'unlimited' || maxLimit === 'Unlimited') {
      return false;
    }

    // Parse maxLimit to number if it's a string
    const maxLimitNum = typeof maxLimit === 'string' ? parseInt(maxLimit, 10) : maxLimit;
    const balanceNum = typeof balance === 'string' ? parseInt(balance, 10) : balance;

    // Check if balance >= maxLimit
    return balanceNum >= maxLimitNum;
  }, [planDetails]);

  const {
    isClarification,
    isReflection,
    isRepeated,
    isPrototyper,
    isReadyToCreate,
    readyToGenerate,
  } = facade.headers;

  // Get current mode from facade
  const { currentMode } = facade.mode;

  // Explore Design Insights Drawer state
  const [isDesignInsightsDrawerOpen, setIsDesignInsightsDrawerOpen] = useState(false);
  const { canBeDrawn, isCreatingDrawing, showBannerPatient, isNeedTime } = facade.drawing;

  const {
    isCreatingPrototype: isPrototypeCreating,
    clearError: clearPrototyperError,
    currentPrototyper,
    fetchPrototyperById,
  } = facade.prototyper;

  const currentPrototypeId = currentPrototyper?.prototyper_demo_link_id ?? null;
  const currentPrototypeUrl = currentPrototyper?.demo_url ?? null;
  const currentPrototypeSlug = currentPrototyper?.slug ?? null;
  const prototypeAudioRef = useRef<HTMLAudioElement | null>(null);
  const previousIsCreatingPrototypeRef = useRef(isPrototypeCreating);

  const { createPrototype } = facade.actions;

  // Check if user has credits remaining
  // Base credits are ALWAYS required first
  // In prototyper mode: also check prototype credits
  const prototypeCredit = planDetails?.credits?.find((c: any) => c.type === 'prototype');
  const prototypeBalance = prototypeCredit != null ? prototypeCredit.balance : 1; // If no prototype credit in planDetails, allow (don't block)

  const baseCredit = planDetails?.credits?.find((c: any) => c.type === 'base');
  const baseBalance =
    baseCredit != null
      ? typeof baseCredit.balance === 'string'
        ? parseInt(baseCredit.balance, 10)
        : (baseCredit.balance ?? 0)
      : 1; // If no base credit in planDetails, allow (don't block)

  // Base credits are always required first
  const hasBaseCredits = baseBalance > 0;
  const hasPrototypeCredits = prototypeBalance > 0;

  // Final check: base credits required, then prototype credits in prototyper mode
  // If no base credits -> block (show UpgradeCtaCard)
  // If has base credits AND in prototyper mode -> also check prototype credits
  const canProceed = hasBaseCredits && (currentMode === 'prototyper' ? hasPrototypeCredits : true);
  // ========== Scroll-to-Bottom Hook ==========
  const {
    showButton: showScrollButton,
    opacity: scrollButtonOpacity,
    scrollToBottom,
  } = useScrollToBottom(
    conversationContainerRef,
    { threshold: 100, debounceMs: 50 },
    messages // Pass messages array directly (not wrapped in array)
  );

  // ========== Effects ==========

  useEffect(() => {
    if (typeof Audio === 'undefined') {
      return;
    }

    const audio = new Audio('https://files.ideoz.ai/mixkit-positive-interface-beep-221.wav');
    audio.preload = 'auto';
    prototypeAudioRef.current = audio;

    return () => {
      audio.pause();
      prototypeAudioRef.current = null;
    };
  }, []);

  // Measure chat box height and update margin-bottom
  useEffect(() => {
    const chatBoxContainer = chatBoxContainerRef.current;
    if (!chatBoxContainer) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        // Add some padding (16px = 4 * 4px) to ensure proper spacing
        setChatBoxHeight(height + 16);
      }
    });

    resizeObserver.observe(chatBoxContainer);

    // Initial measurement
    const initialHeight = chatBoxContainer.offsetHeight;
    setChatBoxHeight(initialHeight + 16);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /**
   * Load conversation history is now handled by the facade
   * The page facade automatically fetches conversation details and loads history
   * when entering with a URL conversation ID
   *
   * This effect is kept for plugin environment which doesn't use facade details fetching
   */
  useEffect(() => {
    // Skip for web - facade handles it
    if (isWeb) {
      return;
    }

    // Plugin only: load history
    if (
      currentConversationId &&
      !isLoading &&
      messages.length === 0 && // Only load if no messages yet (existing conversation)
      !loadedConversationsRef.current.has(currentConversationId)
    ) {
      loadedConversationsRef.current.add(currentConversationId);
      facade.actions.loadHistory(currentConversationId);
    }
  }, [isWeb, currentConversationId, isLoading, messages.length, facade.actions]);

  /**
   * Scroll to bottom after conversation history is loaded
   * Detects when messages go from empty to populated (history just loaded)
   * or when loading transitions complete with messages present
   */
  const prevMessagesLengthRef = useRef(0);
  useEffect(() => {
    const prevLength = prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;

    // Scroll to bottom when messages appear for the first time (0 -> N, history loaded)
    if (prevLength === 0 && messages.length > 0) {
      // Use a delay to ensure DOM has rendered all the messages
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    }
  }, [messages.length, scrollToBottom]);

  /**
   * Handle openConversationAndDrawing user action when conversation loads (Plugin only)
   * If there's a hiddenMessage and canBeDrawn is not set, send the hidden message
   * to trigger the drawing flow
   */
  useEffect(() => {
    // Only run in plugin environment
    if (isWeb) return;

    // Only run once per conversation
    if (hasProcessedUserAction) return;

    // Wait for conversation to be loaded and messages to be present
    if (!currentConversationId || messages.length === 0 || isLoading || isLoadingDetails) return;

    // Check if user has the openConversationAndDrawing action
    const user = facade.header.headerProps.user;
    const userActions = user?.metadata?.userActions;

    if (!userActions || userActions.length === 0) {
      setHasProcessedUserAction(true);
      return;
    }

    const openConversationAction = userActions.find(
      (action: any): action is OpenConversationAndDrawingAction =>
        action.type === 'openConversationAndDrawing' &&
        action.conversationID === currentConversationId
    );

    if (!openConversationAction) {
      setHasProcessedUserAction(true);
      return;
    }

    console.log(
      'Found openConversationAndDrawing action for current conversation:',
      openConversationAction
    );
    setHasProcessedUserAction(true);

    // If canBeDrawn is NOT set and there's a hiddenMessage, send it to trigger drawing
    if (!canBeDrawn && openConversationAction.hiddenMessage) {
      console.log(
        'canBeDrawn not set, sending hidden message to trigger drawing:',
        openConversationAction.hiddenMessage
      );

      // Submit hidden message to trigger the drawing flow
      facade.streaming.createStreamingConversation(
        openConversationAction.hiddenMessage,
        currentConversationId,
        (newConversationId, headers) => {
          // Process headers when streaming completes
          if (headers) {
            facade.headers.processHeaders(
              headers,
              currentConversationId,
              newConversationId,
              undefined,
              urlConversationId,
              facade.state.conversationId || undefined
            );
          }
        },
        undefined, // setIsStreaming - handled internally
        'plugin',
        [], // attachmentIds
        false, // skip_analysis
        true // is_hidden_user_query
      );
    }

    // Clean up the user action from the backend (via facade)
    facade.actions
      .cleanUserActions('openConversationAndDrawing')
      .then(() => {
        console.log('Successfully cleaned openConversationAndDrawing user actions');
      })
      .catch((error: Error) => {
        console.error('Failed to clean user actions:', error);
      });
  }, [
    isWeb,
    currentConversationId,
    messages.length,
    isLoading,
    isLoadingDetails,
    hasProcessedUserAction,
    canBeDrawn,
    facade.header.headerProps.user,
    facade.streaming,
    facade.headers,
    urlConversationId,
    facade.state.conversationId,
  ]);

  /**
   * Reset hasProcessedUserAction when conversation changes
   */
  useEffect(() => {
    setHasProcessedUserAction(false);
  }, [currentConversationId]);

  /** * Play prototype creation sound effect **/
  useEffect(() => {
    const wasCreating = previousIsCreatingPrototypeRef.current;
    previousIsCreatingPrototypeRef.current = isPrototypeCreating;
    const currentPrototypeIsNewlyGenerated = currentPrototyper?.isNewlyGenerated;
    // Play sound only when prototype creation just finished and is newly generated
    if (
      wasCreating &&
      !isPrototypeCreating &&
      currentPrototypeIsNewlyGenerated &&
      currentPrototypeId
    ) {
      const playedKey = `prototype_sound_played_${currentPrototypeId}`;
      if (!localStorage.getItem(playedKey)) {
        const audio = prototypeAudioRef.current;
        if (audio) {
          localStorage.setItem(playedKey, 'true');
          audio.currentTime = 0;
          const playResult = audio.play();
          if (playResult && typeof playResult.catch === 'function') {
            playResult.catch((error) => {
              console.warn('Prototype completion sound could not play automatically:', error);
            });
          }
        }
      }
    }
  }, [isPrototypeCreating, currentPrototypeId]);

  /**
   * Handle conversation visibility with fade-in effect
   */
  useEffect(() => {
    if (isLoading || isLoadingDetails) {
      setShowConversation(true);
      return;
    }

    const timer = setTimeout(() => {
      setShowConversation(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [isLoading, isLoadingDetails]);

  /**
   * Handle project creation readiness
   */
  useEffect(() => {
    if (isReadyToCreate && !isCreateProjectDialogOpen) {
      setIsCreateProjectDialogOpen(true);
      setShowProjectHint(false);
    }
  }, [isReadyToCreate, isCreateProjectDialogOpen]);

  /**
   * Handle prototyper header - open prototype panel when x-prototyper header is received
   */
  useEffect(() => {
    if (
      isPrototyper &&
      currentConversationId &&
      !hasCreatedPrototypeRef.current &&
      !facade.prototyper.isCreatingPrototype
    ) {
      hasCreatedPrototypeRef.current = true;

      // Show toast notification and switch mode ONLY if NOT initiated from uncover mode
      // When initiated from uncover mode, user stays in uncover menu
      if (isWeb && currentMode !== 'prototyper' && !initiatedFromUncover) {
        showToast({
          message: 'Switching to Prototyper Mode...',
          severity: 'success',
          position: 'top-center',
        });
        // Set mode to prototyper
        facade.mode.handleModeChange('prototyper');
      }

      setIsPrototypeOpen(true);

      // Create prototype automatically when header is received
      facade.actions.createPrototype();
    }
  }, [
    isPrototyper,
    currentConversationId,
    facade.actions,
    facade.prototyper.isCreatingPrototype,
    facade.mode,
    currentPrototypeUrl,
    canProceed,
    isWeb,
    currentMode,
  ]);

  /**
   * Reset prototype creation ref when conversation changes
   */
  useEffect(() => {
    hasCreatedPrototypeRef.current = false;
  }, [currentConversationId]);

  /**
   * Clear prototyper state when conversation changes or component unmounts
   */
  useEffect(() => {
    // Clear prototyper state when conversation changes
    return () => {
      // Cleanup on unmount or conversation change
      facade.prototyper.cancelPrototype();
    };
  }, [currentConversationId]); // Remove facade.prototyper from deps

  /**
   * Close prototyper panel when mode changes to uncover
   * BUT only if NOT initiated from uncover mode (user clicking "Generate Prototype" button)
   */
  useEffect(() => {
    if (currentMode === 'uncover' && isPrototypeOpen && !initiatedFromUncover) {
      setIsPrototypeOpen(false);
    }
  }, [currentMode, isPrototypeOpen]);

  /**
   * Smoothly animate panel widths when toggling the prototype view
   */
  useEffect(() => {
    const prototypePanel = prototypePanelRef.current;
    const conversationPanel = conversationPanelRef.current;

    if (!prototypePanel || !conversationPanel) {
      return;
    }

    if (isPrototypeOpen) {
      prototypePanel.expand();
      prototypePanel.resize(PROTOTYPE_PANEL_DEFAULT_PERCENT);
      // Only resize conversation panel if it's not collapsed
      if (!conversationPanel.isCollapsed()) {
        conversationPanel.expand();
        conversationPanel.resize(CONVERSATION_PANEL_DEFAULT_PERCENT);
      }
    } else {
      prototypePanel.collapse();
      conversationPanel.expand();
      conversationPanel.resize(100);
    }

    // Scroll to bottom after panel layout settles
    setTimeout(() => {
      scrollToBottom();
    }, 600);
  }, [isPrototypeOpen]);

  /**
   * Initialize conversation on mount (matching old implementation)
   * When isActive and no messages, submit the initial message and start streaming
   * Use ref to prevent double initialization
   */
  useEffect(() => {
    if (isActive && messages.length === 0 && !hasInitializedRef.current) {
      hasInitializedRef.current = true;

      // Submit initial message with callback for header processing
      if (!currentConversationId && session_id) {
        facade.actions.submitMessage(
          init_message || '',
          initialAttachmentIds,
          (newConversationId, headers) => {
            // Process headers when streaming completes
            if (headers) {
              facade.headers.processHeaders(
                headers,
                currentConversationId,
                newConversationId,
                undefined,
                urlConversationId,
                facade.state.conversationId || undefined
              );
            }
          }
        );
      }
    }
  }, [
    isActive,
    messages.length,
    init_message,
    currentConversationId,
    session_id,
    initialAttachmentIds,
    skip_analysis,
    llmFamily,
    facade.actions,
    facade.streaming,
    isWeb,
  ]);

  /**
   * Close prototype panel
   */
  const handleClosePrototype = useCallback(() => {
    setIsPrototypeOpen(false);
  }, []);

  /**
   * Handle "Generate Prototype" button click from Uncover mode
   * Sends "Generate a prototype" message with prototyper mode to trigger the normal header flow
   * User stays in Uncover mode menu throughout the process
   */
  const handleGeneratePrototypeFromUncover = useCallback(() => {
    if (!currentConversationId) return;

    // Check for prototype credits before proceeding
    if (!hasPrototypeCredits) {
      // Navigate to billing if no prototype credits
      facade.navigation.toBilling();
      return;
    }

    // Mark that this prototype was initiated from uncover mode
    // This prevents the mode switch in the prototyper header effect
    facade.conversation.setInitiatedFromUncover(true);
    // Temporarily switch to prototyper mode for the API request
    facade.conversation.setMode('prototyper');

    // Reset header states before submitting
    facade.headers.setIsClarification(false);
    facade.headers.setIsReflection(false);
    facade.headers.setIsRepeated(false);
    facade.headers.setIsPrototyper(false);
    facade.headers.setIsReadyToCreate(false);
    hasCreatedPrototypeRef.current = false;

    // Submit "Generate a prototype" message with prototyper mode override
    // The modeOverride ensures the API receives 'prototyper' mode even though user stays in uncover menu
    facade.actions.submitMessage(
      'Generate a prototype',
      [],
      (newConversationId, headers) => {
        // Process headers when streaming completes
        // The normal header flow will create the prototype when x-prototyper header is received
        if (headers) {
          facade.headers.processHeaders(
            headers,
            currentConversationId,
            newConversationId,
            undefined,
            urlConversationId,
            facade.state.conversationId || undefined
          );
        }
      },
      'prototyper'
    );
  }, [currentConversationId, facade, hasPrototypeCredits]);

  /**
   * Handle "Back to Uncover" button click
   * Closes the prototype panel and returns to full-width Uncover chat
   */
  const handleBackToUncover = useCallback(() => {
    facade.conversation.setMode('uncover');
    setIsPrototypeOpen(false);
  }, []);

  /**
   * Toggle conversation panel visibility
   */
  const handleToggleConversationPanel = useCallback(() => {
    const conversationPanel = conversationPanelRef.current;
    const prototypePanel = prototypePanelRef.current;

    if (!conversationPanel || !prototypePanel) return;

    const isCollapsed = conversationPanel.isCollapsed();

    if (isCollapsed) {
      // Panel is collapsed, expand it
      conversationPanel.expand();
      // Use setTimeout to ensure expand completes before resizing
      setTimeout(() => {
        conversationPanel.resize(CONVERSATION_PANEL_DEFAULT_PERCENT);
        prototypePanel.resize(PROTOTYPE_PANEL_DEFAULT_PERCENT);
      }, 0);
    } else {
      // Panel is visible, collapse it
      conversationPanel.collapse();
    }
    setTimeout(() => {
      scrollToBottom();
    }, 400);
  }, []);

  /**
   * Reopen prototype panel after user closed it
   */
  const handleReopenPrototype = useCallback(() => {
    setIsPrototypeOpen(true);
  }, []);

  /**
   * Handle Design Insights button click - fetch insights when panel opens
   */
  const handleDesignInsightsClick = useCallback(async () => {
    setIsDesignInsightsPanelOpen(true);

    // Fetch insights if we have project and conversation IDs
    if (currentProjectId && currentConversationId) {
      await facade.designInsights.fetchInsights(currentProjectId, currentConversationId);
    }
  }, [currentProjectId, currentConversationId, facade.designInsights]);

  /**
   * Close Design Insights panel
   */
  const handleCloseDesignInsights = useCallback(() => {
    setIsDesignInsightsPanelOpen(false);
    // Optionally clear insights when closing
    // facade.designInsights.clearInsights();
  }, []);

  /**
   * Handle retry prototyper creation
   */
  const handleRetryPrototyper = useCallback(() => {
    if (currentConversationId) {
      facade.prototyper.clearError();
      facade.actions.createPrototype();
    }
  }, [currentConversationId, facade.actions, facade.prototyper]);

  /**
   * Handle prototyper banner click
   * If prototyper_demo_link_id is provided, fetch the prototyper data
   * Otherwise, just open the panel (already created via header)
   */
  const handlePrototyperClick = useCallback(
    async (prototyperDemoLinkId?: string | number) => {
      // If we have a demo link ID, check if it's different from current
      if (prototyperDemoLinkId) {
        const currentId = currentPrototypeId;

        // Normalize both IDs to numbers for comparison
        const normalizedCurrentId = currentId
          ? typeof currentId === 'string'
            ? parseInt(currentId, 10)
            : currentId
          : null;
        const normalizedRequestedId =
          typeof prototyperDemoLinkId === 'string'
            ? parseInt(prototyperDemoLinkId, 10)
            : prototyperDemoLinkId;

        // Only fetch if it's a different prototyper or we don't have data yet
        if (normalizedCurrentId !== normalizedRequestedId) {
          try {
            await fetchPrototyperById(prototyperDemoLinkId);
            setIsPrototypeOpen(true);
          } catch (error) {
            console.error('Failed to fetch prototyper:', error);
            return;
          }
        } else {
          setIsPrototypeOpen(true);
        }
      } else {
        setIsPrototypeOpen(true);
      }
    },
    [fetchPrototyperById, currentPrototypeId]
  );

  // Inline Design Insights panel width (percentage of the total area)
  const insightsWidthPercent = 25; // adjust as needed

  // ========== Handlers ==========

  /**
   * Show work not saved toast message
   */
  const showWorkNotSavedToast = useCallback(() => {
    showToast({
      message: 'Your all work was not saved, we are sad that you lost your effort.',
      link: 'https://ideoz.ai/docs/whats-an-ideoz-project/',
      linkText: 'Learn more',
      type: 'sorry',
    });
  }, []);

  /**
   * Handle message submission
   */
  const handleSubmit = useCallback(
    (text: string, attachmentIds: string[] = [], llmFamilyParam?: string) => {
      // Validate input
      if (!text.trim() && (!attachmentIds || attachmentIds.length === 0)) {
        console.log('🎯 Validation failed, returning early');
        return;
      }

      // Use provided llmFamily or fall back to state llmFamily
      const selectedLlmFamily = llmFamilyParam || llmFamily;

      // Reset header states
      facade.headers.setIsClarification(false);
      facade.headers.setIsReflection(false);
      facade.headers.setIsRepeated(false);
      facade.headers.setIsPrototyper(false);
      facade.headers.setIsReadyToCreate(false);
      hasCreatedPrototypeRef.current = false;
      // Submit message with callback to process headers
      facade.actions.submitMessage(text, attachmentIds, (newConversationId, headers) => {
        // Process headers when streaming completes
        if (headers) {
          facade.headers.processHeaders(
            headers,
            currentConversationId,
            newConversationId,
            undefined,
            urlConversationId,
            facade.state.conversationId || undefined
          );
        }
      });
    },
    [
      facade.actions,
      facade.headers,
      facade.streaming,
      currentConversationId,
      session_id,
      isWeb,
      llmFamily,
    ]
  );

  /**
   * Handle streaming cancellation
   */
  const handleCancel = useCallback(() => {
    facade.actions.cancelStreaming();
  }, [facade.actions]);

  /**
   * Handle getting project metadata (matching old implementation)
   */
  const handleGetProjectMetadata = useCallback(
    async (conversationId: string, userMessage: string) => {
      setIsLoadingProjectMetadata(true);
      setIsCreateProjectDialogOpen(true);

      if (!conversationId && !userMessage) {
        console.error('No conversation ID or user message available for project metadata');
        setIsLoadingProjectMetadata(false);
        return;
      }

      try {
        await facade.project.getMetadata({
          conversationId,
          userMessage,
        });
        setIsLoadingProjectMetadata(false);
      } catch (error) {
        console.error('Error getting project metadata:', error);
        setIsLoadingProjectMetadata(false);
      }
    },
    [facade.project]
  );

  /**
   * Handle back button click
   * Complex logic from old implementation:
   * - If NO project + logged in → Check project limit → Show upgrade dialog or project creation modal
   * - If NO project + NOT logged in → Show create account dialog
   * - If YES project → Navigate to project details
   */
  const handleBackClick = useCallback(() => {
    setTargetAction(null);
    if (!currentProjectId && isLoggedIn) {
      // No project, but user is logged in
      // Check if user has reached project limit
      if (hasReachedProjectLimit()) {
        // Show upgrade dialog instead of project creation
        setIsUpgradeToSmartDialogOpen(true);
      } else {
        // Show project creation modal
        setIsCreateProjectDialogOpen(true);
        // Get project metadata
        const userMessages = messages.filter((msg) => msg.role === 'user');
        const lastUserMessage = userMessages[userMessages.length - 1]?.content || '';
        if (currentConversationId) {
          handleGetProjectMetadata(currentConversationId, lastUserMessage);
        } else {
          handleGetProjectMetadata(currentConversationId || '', lastUserMessage);
        }
        setShowProjectHint(true);
      }
    } else if (!currentProjectId && !isLoggedIn) {
      // No project and not logged in - show create free account dialog
      setIsCreateFreeAccountDialogOpen(true);
    } else {
      // Has project - navigate to project details
      facade.actions.navigateToProjectDetails(currentProjectId!);
      // handleCancel(); // Stop any ongoing operations
    }
    handleCancel();
  }, [
    currentProjectId,
    isLoggedIn,
    isWeb,
    authDialogState,
    pluginLogin,
    facade.actions,
    handleCancel,
    currentConversationId,
    messages,
    handleGetProjectMetadata,
    hasReachedProjectLimit,
  ]);

  /**
   * Intercept browser back button to show custom dialog
   * When user clicks browser back button, prevent navigation and show custom dialog
   */
  useEffect(() => {
    // Only intercept on web platform
    if (!isWeb || typeof window === 'undefined') {
      return;
    }

    // Check if we're in a plugin context (data: URL or null origin)
    const isPluginContext =
      window.location.origin === 'null' ||
      window.location.protocol === 'data:' ||
      window.location.href.startsWith('data:');

    if (isPluginContext) {
      return;
    }

    try {
      // Push a state when component mounts to enable back button interception
      window.history.pushState(null, '', window.location.href);
    } catch (error) {
      // Silently fail in plugin context where pushState is not supported
      console.debug('pushState not available:', error);
      return;
    }

    const handlePopState = () => {
      // Show custom dialog instead of navigating back
      handleBackClick();

      try {
        // Push current state back to prevent navigation
        // This keeps the user on the current page
        window.history.pushState(null, '', window.location.href);
      } catch (error) {
        // Silently fail if pushState is not available
        console.debug('pushState not available in popstate handler:', error);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isWeb, handleBackClick]);

  /**
   * Handle banner action (create project)
   */
  const handleBannerAction = useCallback(() => {
    setTargetAction('create_project');
    if (isLoggedIn) {
      // Check if user has reached project limit
      if (hasReachedProjectLimit()) {
        // Show upgrade dialog instead of project creation
        setIsUpgradeToSmartDialogOpen(true);
      } else {
        // Get project metadata
        const userMessages = messages.filter((msg) => msg.role === 'user');
        const lastUserMessage = userMessages[userMessages.length - 1]?.content || '';
        handleGetProjectMetadata(currentConversationId || '', lastUserMessage);
      }
    } else {
      // Open auth dialog/flow
      setIsCreatingAccount(true);
      setIsCreateFreeAccountDialogOpen(true);
      setShowProjectHint(false);
    }
  }, [
    isLoggedIn,
    isWeb,
    authDialogState,
    pluginLogin,
    currentConversationId,
    messages,
    handleGetProjectMetadata,
    hasReachedProjectLimit,
  ]);

  /**
   * Handle project creation
   */
  const handleCreateProject = useCallback(
    async (projectName: string) => {
      if (currentConversationId) {
        setIsCreatingProject(true);
        try {
          const projectData = {
            name: projectName,
            tmp_conversation_id: currentConversationId,
            description: facade.project.projectMetaData?.description || '',
            ideoz_role: facade.project.projectMetaData?.ideoz_role || '',
            main_persona: facade.project.projectMetaData?.persona || '',
            industry: facade.project.projectMetaData?.industry || '',
          };

          const result = await facade.project.create(projectData);

          if (result.type === 'project/create/fulfilled') {
            const projectId = result.payload?.project_id || result.payload?.data?.project_id;

            if (projectId) {
              setIsCreateProjectDialogOpen(false);
              facade.project.setCurrent(projectId);
              facade.navigation.toProjectDetails(projectId.toString());
              setIsCreatingProject(false);

              showToast({
                message:
                  'Your project created successfully. Add more context to get sharper insights.',
                severity: 'success',
              });
              facade.project.setDrawerVisibility(true);
            } else {
              console.error('No project ID returned from API');
              setIsCreatingProject(false);
            }
          } else {
            console.error('Failed to create project:', result.error);
            setIsCreatingProject(false);
          }
        } catch (err) {
          setIsCreatingProject(false);
          const error = err as Error;
          showToast({
            message: error.message || 'Failed to create project',
            severity: 'error',
          });
        }
      }
    },
    [currentConversationId, facade, showToast]
  );

  /**
   * Handle drawing retry (including prototyper retry)
   */
  const handleRetryDrawing = useCallback(
    (drawType: string) => {
      if (!currentConversationId) return;

      if (drawType === 'prototyper') {
        // Retry prototyper creation
        handleRetryPrototyper();
      } else {
        // Retry drawing creation
        facade.actions.createDrawing(drawType);
      }
    },
    [currentConversationId, facade.actions, handleRetryPrototyper]
  );

  /**
   * Handle feedback (like/dislike)
   */
  const handleFeedback = useCallback(
    async (type: 'like' | 'dislike', messageId?: string | null, projectId?: string | null) => {
      if (!messageId) return;
      await facade.actions.sendFeedback(type, messageId, projectId);
    },
    [facade.actions]
  );

  /**
   * Handle action clicks from messages
   */
  const handleActionClick = useCallback(
    (action: string, message?: string) => {
      switch (action) {
        case 'createProject':
          setTargetAction('create_project');
          if (isLoggedIn) {
            // Check if user has reached project limit
            if (hasReachedProjectLimit()) {
              // Show upgrade dialog instead of project creation
              setIsUpgradeToSmartDialogOpen(true);
            } else {
              // Get last user message content
              const userMessages = messages.filter((msg) => msg.role === 'user');
              const lastUserMessage = userMessages[userMessages.length - 1]?.content || '';
              handleGetProjectMetadata(currentConversationId || '', lastUserMessage);
            }
          } else {
            setIsCreatingAccount(true);
            setIsCreateFreeAccountDialogOpen(true);
            setShowProjectHint(false);
          }
          break;
        case 'generateHere':
          if (message) {
            handleSubmit(message);
          }
          break;
        case 'drawingInFigJam':
          // Handle FigJam plugin action
          // Check if user has plugin metadata (web environment)
          const user = facade.header.headerProps.user;
          if (user && (user as any).metadata?.isHasPlugin) {
            // User has plugin, show the manual plugin dialog
            setIsFigJamPluginDialogOpen(true);
          } else if (isWeb) {
            // User doesn't have plugin, open community page
            window.open(
              'https://www.figma.com/community/plugin/1403437895117374709/ideoz-ai-powered-tool-to-empower-you',
              '_blank'
            );
          }
          break;
        default:
          break;
      }
    },
    [
      handleSubmit,
      handleGetProjectMetadata,
      isLoggedIn,
      currentConversationId,
      messages,
      hasReachedProjectLimit,
    ]
  );

  /**
   * Handle create free account action (from dialog)
   */
  const handleCreateFreeAccount = useCallback(() => {
    setIsCreateFreeAccountDialogOpen(false);
    if (!isLoggedIn) {
      if (isWeb) {
        authDialogState?.openRegister();
      } else {
        setWaitingForAuth(true);
        pluginLogin?.('register');
      }
    }
  }, [isLoggedIn, isWeb, authDialogState, pluginLogin]);

  /**
   * Handle publish prototype
   */
  const handlePublishPrototype = useCallback(
    async (slug: string | null) => {
      const prototyperId = currentPrototypeId;

      if (!prototyperId) {
        showToast({
          message: 'No prototype ID available',
          severity: 'error',
        });
        return;
      }

      try {
        await facade.prototyper.updatePrototyperDemoLink(prototyperId, slug || null);
      } catch (error) {
        console.error('Error publishing prototype:', error);
        showToast({
          message: 'Failed to publish prototype',
          severity: 'error',
        });
      }
    },
    [facade.prototyper, showToast, currentPrototypeId]
  );

  /**
   * Handle FigJam plugin action - open FigJam and set up user action
   */
  const handleOpenFigJamPlugin = useCallback(async () => {
    try {
      setIsFigJamPluginDialogOpen(false);

      // Call the facade action to add user action
      await facade.actions.handleFigJamAction(
        currentConversationId || '',
        name || 'New Conversation',
        canBeDrawn || readyToGenerate || null,
        currentProjectId || null,
        !canBeDrawn
          ? `generate ${(readyToGenerate || '').replace(/_/g, ' ').replace('node', '')}`
          : null
      );

      // Open FigJam in new tab
      window.open('https://www.figma.com/board/new?kind=figjam&app=desktop', '_blank');
    } catch (error) {
      console.error('Failed to add user action:', error);
      setIsFigJamPluginDialogOpen(false);
    }
  }, [facade.actions, currentConversationId, name, canBeDrawn, readyToGenerate, currentProjectId]);

  /**
   * Handle cancel FigJam plugin dialog
   */
  const handleCancelFigJamPlugin = useCallback(() => {
    setIsFigJamPluginDialogOpen(false);
  }, []);

  /**
   * Handle close free account dialog
   */
  const handleCloseFreeAccountDialog = useCallback(
    (value?: 'close-icon' | 'back-button') => {
      setIsCreateFreeAccountDialogOpen(false);
      // Reset creating account state when dialog closes
      setIsCreatingAccount(false);

      // Handle back button click (only if not creating account - to avoid toast after successful creation)
      if (value === 'back-button' && !isLoggedIn) {
        // User clicked back button before completing account creation - navigate to landing
        showWorkNotSavedToast();
        facade.navigation.toLanding?.();
      }
    },
    [isLoggedIn, facade.navigation, showWorkNotSavedToast]
  );

  /**
   * Reset isCreatingAccount when user successfully logs in/registers
   */
  useEffect(() => {
    if (isLoggedIn && isCreatingAccount) {
      setIsCreatingAccount(false);
    }
  }, [isLoggedIn, isCreatingAccount]);

  // ========== Computed Values ==========

  const backRoute = currentProjectId ? `/projects/${currentProjectId}` : '/';

  const shouldShowBanner = () => {
    const userMessages = messages.filter((msg) => msg.role === 'user');
    return !currentProjectId && userMessages.length >= 2 && showBanner;
  };

  // Set banner properties on last message if there's a prototyper error
  const messagesWithBanner = React.useMemo(() => {
    if (!facade.prototyper.prototypeError || messages.length === 0) return messages;

    const lastMessage = messages[messages.length - 1];
    const hasPrototyperMode =
      (lastMessage as any).mode === 'prototyper' || (lastMessage as any).prototyper_demo_link_id;

    if (!hasPrototyperMode) return messages;

    // Clone messages array and update last message with banner properties
    const updatedMessages = [...messages];
    updatedMessages[updatedMessages.length - 1] = {
      ...lastMessage,
      hasBanner: true,
      bannerType: 'error',
      bannerPosition: 'bottom',
      drawType: 'prototyper',
    } as any;

    return updatedMessages;
  }, [messages, facade.prototyper.prototypeError]);

  const showCreateButton = (() => {
    const hasAssistantMessage = messages.some((message) => message.role === 'assistant');
    const lastMessage = messages[messages.length - 1];
    const isLastMessageFromAssistant = lastMessage?.role === 'assistant';
    const isLastAssistantMessageComplete =
      isLastMessageFromAssistant && lastMessage?.content && lastMessage.content.trim().length > 0;
    const userMessagesCountGreaterThanOne =
      messages.filter((message) => message.role === 'user').length > 1;

    return (
      !currentProjectId &&
      hasAssistantMessage &&
      !userMessagesCountGreaterThanOne &&
      !isLoading &&
      !isStreaming &&
      isLastAssistantMessageComplete
    );
  })();

  // const getLastMessageMinHeight = (index: number): string => {
  //   if (index !== messages.length - 1) return '';

  //   if (isWeb) {
  //     return shouldShowBanner && !isReadyToCreate
  //       ? 'min-h-[calc(100vh-360px)]'
  //       : 'min-h-[calc(100vh-300px)]';
  //   } else {
  //     return shouldShowBanner && !isReadyToCreate
  //       ? 'min-h-[calc(100vh-272px)]'
  //       : 'min-h-[calc(100vh-227px)]';
  //   }
  // };

  const conversationDirection = React.useMemo<'rtl' | 'ltr' | undefined>(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.detectedLanguage) {
        return message.detectedLanguage;
      }

      if (message.content && hasArabicCharacters(message.content)) {
        return 'rtl';
      }
    }

    if (name && hasArabicCharacters(name)) {
      return 'rtl';
    }

    if (init_message && hasArabicCharacters(init_message)) {
      return 'rtl';
    }

    return undefined;
  }, [messages, name, init_message]);

  // ========== Render ==========

  return (
    <>
      {/* Loading Overlay (Plugin Only) */}
      {!isWeb && pluginStopInterval && (
        <LoadingOverlay isLoading={isAuthLoading} onCancel={pluginStopInterval} />
      )}

      <div
        className="bg-white"
        style={
          {
            '--sidebar-width': isSidebarCollapsed
              ? 'var(--sidebar-width-collapsed)'
              : 'var(--sidebar-width-expanded)',
          } as React.CSSProperties
        }
      >
        {/* Sidebar */}
        <SideMenu
          items={(() => {
            let items = facade.sidebar.navigationItems || [];
            if (isOnboardingUser) {
              // Remove mode items
              items = items.filter((item) => item.type !== 'mode');
              // Add 'new chat' item if not present
              if (!items.some((item) => item.id === 'new-chat')) {
                items = [
                  {
                    id: 'new-chat',
                    label: 'New Chat',
                    icon: CirclePlus,
                    type: 'navigation',
                    onClick: () => {
                      // Act like back arrow: use the same handler
                      handleBackClick();
                    },
                  },
                  ...items,
                ];
              }
            }
            return items;
          })()}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          socialLinks={facade.sidebar.socialLinks}
          onLogoClick={facade.sidebar.onNavigateToLanding}
          user={{
            isLoggedin: isLoggedIn,
            name: facade.header.headerProps.user?.name,
            avatar: facade.header.headerProps.user?.avatar,
          }}
          userMenuProps={{
            points: facade.header.headerProps.points,
            pointsLabel: 'Points',
            menuItems: facade.header.userMenuItems || [],
            socialLinks: facade.header.userMenuSocialLinks || [],
            onRequestUpgrade: facade.header.onRequestUpgrade,
            isRequestingUpgrade: facade.header.isRequestingUpgrade || false,
            canRequestUpgrade: true,
            isWeb,
          }}
        />

        {/* Main Content */}
        <main
          className={cn(
            `flex-1 overflow-auto transition-[margin-left] duration-200 ease-in-out`,
            'ml-[var(--sidebar-width)]'
          )}
        >
          {/* Header */}
          <Header
            {...facade.header.headerProps}
            showBackButton
            backRoute={backRoute}
            backText={name || 'New Conversation'}
            onBack={handleBackClick}
            isWeb={isWeb}
            className={cn(
              ` fixed top-0 left-[var(--sidebar-width)] right-0 z-10  transition-all duration-200 ease-in-out`,
              isDesignInsightsPanelOpen ? 'right-[430px]' : 'right-0'
            )}
            creditLimitBanner={
              creditLimitBanner.isVisible
                ? {
                    ...creditLimitBanner,
                    onAction: () => facade.navigation.toBilling(),
                    onDismiss: () => creditLimitBanner.setVisible(false),
                  }
                : undefined
            }
          />

          {/* Banner */}
          <AnimatedBanner
            className={`ml-0 ${creditLimitBanner?.isVisible ? (isWeb ? 'mt-[70px]' : 'mt-[30px]') : ''}`}
            isVisible={shouldShowBanner() && !isReadyToCreate}
            onClose={() => {
              setShowBanner(false);
            }}
            onAction={handleBannerAction}
            icon={Sparkles}
            text="Want to go deeper? Create a project now for better context results"
            actionText={isLoggedIn ? 'Create project' : 'Create free account'}
            bgColor="bg-success-100/80"
            showCloseButton={true}
          />

          {/* Main Content - Split or Full */}
          <ResizablePanelGroup direction="horizontal" className="flex-1 !h-screen">
            {/* Conversation Panel */}
            <ResizablePanel
              ref={conversationPanelRef}
              defaultSize={isPrototypeOpen ? CONVERSATION_PANEL_DEFAULT_PERCENT : 100}
              minSize={0}
              maxSize={isPrototypeOpen ? 50 : 100}
              collapsible
              collapsedSize={0}
              onCollapse={() => setIsConversationPanelCollapsed(true)}
              onExpand={() => setIsConversationPanelCollapsed(false)}
              className="!overflow-auto"
            >
              <div
                className={`relative max-w-landing-page mx-auto p-6 items-center ${isWeb ? 'min-h-[calc(100vh-theme(spacing.header))] mt-[theme(spacing.header)]' : 'min-h-[calc(100vh-theme(spacing.plugin-header))] mt-[theme(spacing.plugin-header)]'} ${isPrototypeOpen ? 'max-h-[calc(100vh-theme(spacing.header))]' : ''}`}
              >
                {/* Skeleton Loading */}
                {!showConversation && (
                  <div className="flex justify-center w-full py-4">
                    <ConversationSkeleton messageCount={1} />
                  </div>
                )}

                {/* Messages Container */}
                <div
                  className={cn(
                    `relative overflow-auto h-[calc(100vh-270px)] ${isPrototypeOpen && !canProceed ? '!mb-0 h-[calc(100vh-310px)]' : isPrototypeOpen ? `!mb-0 h-[calc(100vh-240px)]` : ''}`
                  )}
                  style={{ marginBottom: `${chatBoxHeight}px` }}
                >
                  <div
                    ref={conversationContainerRef}
                    data-testid="chat-messages-container"
                    className={`conversation-container overflow-auto justify-center w-full mb-4 [scrollbar-gutter:stable_both-edges] scrollbar-chat overflow-y-auto ${
                      showConversation ? 'opacity-100' : 'opacity-0'
                    }`}
                    // style={{
                    //   visibility: showConversation ? 'visible' : 'hidden',
                    // }}
                  >
                    <div className="">
                      <MessageList
                        messages={messagesWithBanner}
                        projectId={currentProjectId}
                        isLoading={isLoading}
                        isStreaming={isStreaming}
                        isClarification={isClarification}
                        isReflection={isReflection}
                        isRepeated={isRepeated}
                        isReadyToCreate={isReadyToCreate}
                        canBeDrawn={canBeDrawn}
                        readyToGenerate={readyToGenerate}
                        showBannerPatient={showBannerPatient}
                        isNeedTime={isNeedTime}
                        isCreatingDrawing={isCreatingDrawing}
                        showCreateButton={showCreateButton}
                        shouldShowBanner={shouldShowBanner()}
                        isWeb={isWeb}
                        isLoggedIn={isLoggedIn}
                        isCreatingAccount={isCreatingAccount}
                        onPrototypeReturn={handleReopenPrototype}
                        onPrototyperClick={handlePrototyperClick}
                        onSubmit={handleSubmit}
                        onActionClick={handleActionClick}
                        onFeedback={handleFeedback}
                        setIsReflection={facade.headers.setIsReflection}
                        setIsRepeated={facade.headers.setIsRepeated}
                        onRetryDrawing={handleRetryDrawing}
                        forcedDirection={conversationDirection}
                        isPrototypeOpen={isPrototypeOpen}
                      />
                    </div>
                  </div>

                  {/* Scroll to Bottom Button */}
                  <GoToBottomButton
                    isVisible={showScrollButton}
                    opacity={scrollButtonOpacity}
                    onClick={scrollToBottom}
                    className={cn(
                      isPrototypeOpen
                        ? `fixed bottom-40 ${isSidebarCollapsed ? 'left-24' : 'left-[300px]'}`
                        : 'fixed bottom-40 left-1/2 -translate-x-1/2 z-50'
                    )}
                  />
                </div>

                {/* Input Area */}
                <div
                  ref={chatBoxContainerRef}
                  className={`glass border-none bg-transparent left-0 transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'left-[75px]' : 'left-[270px]'} ${isPrototypeOpen ? 'static' : 'fixed bottom-0 right-0 pb-2'}`}
                >
                  <div className="max-w-[830px] mx-auto relative">
                    {canProceed ? (
                      <ChatTextArea
                        onSubmit={handleSubmit}
                        placeholder={
                          currentMode === 'prototyper'
                            ? 'Describe your user experience prototype to generate …'
                            : 'Describe your UX challenge, ask questions, or request analysis...'
                        }
                        disabled={isLoading || isPrototypeCreating}
                        isChatLoading={
                          isLoading || isStreaming || isCreatingDrawing || isPrototypeCreating
                        }
                        onCancel={handleCancel}
                        useFileAttachmentsHook={useFileAttachments}
                        isWeb={isWeb}
                        initialLlmFamily={llmFamily as any}
                        onLlmFamilyChange={(newModel) => {
                          // Update Redux state when model changes
                          facade.conversation.setLlmFamily(newModel);
                        }}
                        data-testid="conversation-chat-textarea"
                        isPrototypeOpen={isPrototypeOpen}
                        rows={1}
                        isPrototypeCreating={isPrototypeCreating}
                        currentMode={currentMode}
                        onGeneratePrototype={handleGeneratePrototypeFromUncover}
                        onBackToUncover={handleBackToUncover}
                        hasPrototypeCredits={hasPrototypeCredits}
                        hasProject={!!currentProjectId}
                        initiatedFromUncover={initiatedFromUncover}
                        prototyperGenerationModel={facade.prototyper.prototypeGenerationModel}
                        onPrototyperGenerationModelChange={
                          facade.prototyper.setPrototypeGenerationModel
                        }
                      />
                    ) : (
                      <UpgradeCtaCard
                        title={
                          !hasBaseCredits
                            ? "You've used all your credits"
                            : "You've used all your prototype credits"
                        }
                        description={
                          !hasBaseCredits
                            ? 'Upgrade your plan or purchase additional credits to continue.'
                            : 'Upgrade your plan or purchase additional prototype credits to continue.'
                        }
                        buttonText="Upgrade"
                        onUpgrade={facade.navigation.toBilling}
                      />
                    )}
                  </div>
                </div>
              </div>
            </ResizablePanel>

            {/* Resize Handle */}
            {isPrototypeOpen && <ResizableHandle withHandle />}

            {/* Prototype Panel (uses PrototyperPanel organism) */}
            <ResizablePanel
              ref={prototypePanelRef}
              defaultSize={isPrototypeOpen ? PROTOTYPE_PANEL_DEFAULT_PERCENT : 0}
              minSize={50}
              maxSize={100}
              collapsible
              collapsedSize={0}
              className={`${isPrototypeOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={!isPrototypeOpen}
              onCollapse={() => setIsConversationPanelCollapsed(false)}
            >
              <PrototyperPanel
                isOpen={isPrototypeOpen}
                onClose={handleClosePrototype}
                shouldShowBanner={shouldShowBanner}
                isReadyToCreate={isReadyToCreate}
                iframeSrc={currentPrototypeUrl || undefined}
                isLoading={facade.prototyper.isCreatingPrototype}
                onPublish={handlePublishPrototype}
                prototyperDemoLinkId={currentPrototypeId || undefined}
                prototyperSlug={currentPrototypeSlug || undefined}
                userName={facade.header.headerProps.user?.name}
                platformLink={ApiUrls.platformLink}
                isWeb={isWeb}
                onDesignInsightsClick={currentProjectId ? handleDesignInsightsClick : undefined}
                onToggleConversationPanel={handleToggleConversationPanel}
                isConversationPanelCollapsed={isConversationPanelCollapsed}
              />
            </ResizablePanel>
            {isDesignInsightsPanelOpen && (
              <DesignInsightsPanel
                open={isDesignInsightsPanelOpen}
                onClose={handleCloseDesignInsights}
                inline
                data={facade.designInsights.insights || undefined}
                isLoading={facade.designInsights.isLoading}
              />
            )}
          </ResizablePanelGroup>
        </main>
      </div>

      {/* Project Creation Modal */}
      <ConfirmProjectModal
        isOpen={isCreateProjectDialogOpen}
        showProjectHint={showProjectHint}
        initialProjectName={facade.project.projectMetaData?.title || ''}
        isProjectNameLoading={isLoadingProjectMetadata}
        onClose={(source?: 'close-icon' | 'back-button') => {
          setIsCreateProjectDialogOpen(false);
          facade.headers.setIsReadyToCreate(false);
          setShowBanner(true);
          setShowProjectHint(false);
          setTargetAction(null);
          // If no project ID and no targetAction, navigate to landing when closing modal (back-button)
          if (!currentProjectId && !targetAction && source === 'back-button') {
            trackEvent('tmp_chat_abandoned', {
              conversation_id: currentConversationId ?? undefined,
              exit_source: 'create_project_modal',
            });
            facade.navigation.toLanding?.();
            handleCancel();
            showWorkNotSavedToast();
          }
          if (showProjectHint) {
            setShowProjectHint(false);
          }
        }}
        onCreate={handleCreateProject}
        title="Create new project"
        description="Give your project a clear, focused name. e.g., 'Checkout drop-off issue'"
        info={{
          title: 'What is project?',
          desc: 'Projects help you organize your thoughts, stay in flow, and come back anytime without starting over.',
        }}
        buttonText={isLoggedIn ? 'Create project' : 'Create free account'}
        buttonType="primary"
        iconName={FolderPlus}
        isLoading={isCreatingProject}
      />

      {/* Create Free Account Dialog */}
      <CreateFreeAccountConversationDialog
        isOpen={isCreateFreeAccountDialogOpen}
        onClose={handleCloseFreeAccountDialog}
        onCreate={handleCreateFreeAccount}
        isWeb={isWeb}
      />

      {/* Auth Dialog (Web Only) */}
      {isWeb && authDialogState && (
        <AuthDialog
          isOpen={authDialogState.isOpen}
          mode={authDialogState.mode}
          onClose={authDialogState.closeDialog}
          onModeChange={authDialogState.switchMode}
          onLogin={authDialogState.handleLogin}
          onRegister={authDialogState.handleRegister}
          onGoogleAuth={authDialogState.handleGoogleAuth}
          isLoading={authDialogState.isLoading}
          onNavigateToForgotPassword={authDialogState.handleNavigateToForgotPassword}
        />
      )}

      {/* FigJam Plugin Dialog */}
      <FigJamPluginDialog
        isOpen={isFigJamPluginDialogOpen}
        onClose={() => setIsFigJamPluginDialogOpen(false)}
        onOpenFigJamPlugin={handleOpenFigJamPlugin}
        onCancel={handleCancelFigJamPlugin}
      />

      {/* Upgrade to Smart Dialog */}
      <UpgradeToSmartDialog
        isOpen={isUpgradeToSmartDialogOpen}
        onClose={() => {
          if (!currentProjectId && !targetAction) {
            trackEvent('tmp_chat_abandoned', {
              conversation_id: currentConversationId ?? undefined,
              exit_source: 'upgrade_dialog_closed',
            });
            facade.navigation.toLanding?.();
            handleCancel();
            showWorkNotSavedToast();
          }
          setIsUpgradeToSmartDialogOpen(false);
        }}
        onConfirm={() => {
          setIsUpgradeToSmartDialogOpen(false);
          facade.navigation.toBilling();
        }}
        variant="project-limit"
        currentPlanName={planDetails?.name || 'Free'}
        currentPlanPrice="$0/month"
        currentPlanProjects={(() => {
          const projectsFeature = planDetails?.features?.find((f: any) => f.name === 'Projects');
          if (!projectsFeature) return '1 Project';
          const maxLimit = projectsFeature.maxLimit;
          if (maxLimit === '1' || maxLimit === 1) return '1 Project';
          return `${maxLimit} Projects`;
        })()}
        upgradedPlanName="Smart"
        upgradedPlanPrice="$19/month"
      />
    </>
  );
}

export default ConversationPageTemplate;
